import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Hero from "@/components/public/Hero";
import StorySection from "@/components/public/StorySection";
import LocationMap from "@/components/public/LocationMap";
import WeatherForecast from "@/components/public/WeatherForecast";
import DressCode from "@/components/public/DressCode";
import ProgramTimeline from "@/components/public/ProgramTimeline";
import AccommodationSection from "@/components/public/AccommodationSection";
import Gallery from "@/components/public/Gallery";
import UploadModal from "@/components/public/UploadModal";
import SongRequests from "@/components/public/SongRequests";
import TableFinder from "@/components/public/TableFinder";
import FaqSection from "@/components/public/FaqSection";
import GiftSection from "@/components/public/GiftSection";
import RsvpForm from "@/components/public/RsvpForm";
import Reveal from "@/components/shared/Reveal";
import { trackEvent } from "@/lib/track";

export default function PublicWedding() {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [error, setError] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    api
      .get(`/public/${slug}`)
      .then((r) => {
        setWedding(r.data);
        const theme = r.data.tema_colore || {};
        if (theme.primary) document.documentElement.style.setProperty("--wed-primary", theme.primary);
        if (theme.font) document.documentElement.style.setProperty("--wed-font", `'${theme.font}', serif`);
        document.title = r.data.nome_sposi;
        trackEvent(slug, "page_view");
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="wedding-not-found">
        <p className="font-wed text-2xl text-center">Evento non trovato</p>
      </div>
    );

  if (!wedding)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-wed text-xl" style={{ color: "var(--wed-primary)" }}>
          Caricamento…
        </div>
      </div>
    );

  // RSVP stays open until `rsvp_cutoff_days` before the event (default 7) -- by
  // then the guest list is considered final, in line with how real weddings close
  // RSVPs well before the day. The couple can also turn it off entirely.
  const daysUntilEvent = (new Date(wedding.data_evento).getTime() - Date.now()) / 86_400_000;
  const showRsvp = wedding.rsvp_enabled !== false && daysUntilEvent > (wedding.rsvp_cutoff_days ?? 7);

  // Sections already hide themselves when there's no content to show (empty
  // dress code text, no FAQ items, etc.) -- this is a separate, explicit
  // on/off switch from Settings that overrides that, for a section the couple
  // wants hidden regardless of content.
  const sections = wedding.sections_enabled || {};
  const show = (key) => sections[key] !== false;

  // Guests arriving only for the evening shouldn't need (or see) the seating
  // chart or the day's schedule once the reception is underway -- both
  // disappear together at `tables_hide_at`. Before the ceremony, the seating
  // chart isn't assigned/final yet anyway, so it stays blurred/locked instead
  // of just empty, with the unlock time shown instead of a bare search box.
  const now = Date.now();
  const unlockAt = wedding.tables_unlock_at || wedding.data_evento;
  const hideAt = wedding.tables_hide_at;
  const tablesAndProgramHidden = !!hideAt && now >= new Date(hideAt).getTime();
  const tableFinderLocked = !tablesAndProgramHidden && now < new Date(unlockAt).getTime();

  return (
    <div className="grain" data-testid="public-wedding-page">
      <Hero wedding={wedding} onOpenUpload={() => setUploadOpen(true)} />
      {show("story") && <Reveal><StorySection wedding={wedding} /></Reveal>}
      {show("location") && <Reveal><LocationMap wedding={wedding} /></Reveal>}
      {show("weather") && <Reveal><WeatherForecast wedding={wedding} /></Reveal>}
      {show("dress_code") && <Reveal><DressCode wedding={wedding} /></Reveal>}
      {show("program") && !tablesAndProgramHidden && <Reveal><ProgramTimeline program={wedding.program} /></Reveal>}
      {show("accommodation") && <Reveal><AccommodationSection wedding={wedding} /></Reveal>}
      {show("gallery") && <Reveal><Gallery wedding={wedding} onOpenUpload={() => setUploadOpen(true)} /></Reveal>}
      {show("songs") && <Reveal><SongRequests slug={slug} wedding={wedding} /></Reveal>}
      {show("table_finder") && !tablesAndProgramHidden && <Reveal><TableFinder slug={slug} locked={tableFinderLocked} unlockAt={unlockAt} /></Reveal>}
      {show("faq") && <Reveal><FaqSection wedding={wedding} /></Reveal>}
      {show("gift") && <Reveal><GiftSection wedding={wedding} /></Reveal>}
      {show("rsvp") && showRsvp && <Reveal><RsvpForm slug={slug} wedding={wedding} /></Reveal>}
      <UploadModal slug={slug} open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <footer className="py-12 text-center text-sm text-neutral-400">
        <p className="font-wed text-lg mb-1" style={{ color: "var(--wed-primary)" }}>
          {wedding.nome_sposi}
        </p>
        <p>{new Date(wedding.data_evento).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</p>
      </footer>
    </div>
  );
}
